<template>
  <div>
    <mb-modal
      ref="marker-edit"
      :title="editMarker.id ? 'Edit Marker' : 'Create Marker'"
      size="small"
      @on-close="cancelEdit"
    >
      <form>
        <div class="container p-2">
          <div class="row mb-3 align-items-center">
            <div class="col-4 text-end">
              <label for="marker-label" class="form-label">Marker Label</label>
            </div>
            <div class="col-8">
              <input ref="edit-label" type="text" id="marker-label" class="form-control" v-model="editMarker.label" />
            </div>
          </div>
          <div class="row mb-3 align-items-center">
            <div class="col-4 text-end">
              <label for="marker-time" class="form-label">Time</label>
            </div>
            <div class="col-8">
              <input
                type="text"
                id="marker-time"
                class="form-control"
                :class="{ 'is-invalid': timeError }"
                v-model="editMarker.timeText"
              />
              <div v-if="timeError" class="invalid-feedback d-block">{{ timeError }}</div>
            </div>
          </div>
          <div class="row mb-3 align-items-center">
            <div class="col offset-4">
              <button type="submit" class="btn btn-primary me-2" @click.prevent="saveMarker(editMarker)">Save</button>
              <a href="#" v-if="editMarker.id" @click.prevent="deleteMarker(editMarker)" class="btn btn-danger"
                >Delete Marker</a
              >
            </div>
          </div>
        </div>
      </form>
    </mb-modal>
    <div
      class="marker-lane"
      ref="markerLane"
      :class="{ 'marker-lane-readonly': !canEditMarkers }"
      @click.prevent="createMarker"
    >
      <div class="marker-lane-ruler" :style="rulerBackground"></div>
      <div v-for="marker in markers" :key="marker.id">
        <div class="marker" :style="markerStyle(marker)">
          <img
            src="/images/marker.svg"
            :class="{ 'marker-delete-cursor': altKeyDown && canEditMarkers }"
            @click.stop.prevent="eatClick"
            @mousedown.stop.prevent="mouseDown(marker, $event)"
          />
          <div>{{ marker.label }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.marker-lane {
  display: flex;
  height: 20px;
  border-radius: 3px;
  position: relative;
  margin-left: 50px;
  margin-right: 30px;
  cursor:
    url("../../../public/images/cursor_plus.svg") 0 0,
    auto;
}

.marker-lane.marker-lane-readonly {
  cursor: auto;
}

.marker-lane-ruler {
  height: 12px;
  position: absolute;
  top: 0;
  width: 100%;
}

@media (max-width: 992px) {
  .marker-lane {
    margin-left: 20px;
    margin-right: 30px;
  }
}

@media (max-width: 576px) {
  .marker-lane {
    background: none !important;
    margin-left: 0px;
    margin-right: 0px;
  }
}

.marker {
  display: flex;
  position: absolute;
  font-size: 0.8rem;
  align-items: baseline;
  height: 20px;
}

.marker img {
  height: 20px;
  position: relative;
  cursor: pointer;
}

.marker img.marker-delete-cursor {
  cursor:
    url("../../../public/images/cursor_minus.svg") 0 0,
    auto;
}

.marker div {
  display: inline-block;
  padding-left: 3px;
  font-size: 12px;
  color: #333;
  bottom: 2px;
  position: relative;
  background: white;
}
</style>

<script>
const DBLCLICK_WINDOW = 300 // ms
const INIT_MOUSE = {
  downMarker: undefined,
  clicks: 0,
  mouseDownTime: undefined,
  mouseDownX: undefined,
  dragMarker: undefined,
  dragStart: undefined,
}

import MBModal from "./modal.vue"
import { post_csrf } from "../utils/csrf"
import timeFormat, { parseTimeText } from "../utils/time_format.js"
import mixChannelSubscription from "./mix_channel_subscription"

export default {
  components: { "mb-modal": MBModal },
  props: ["mixID", "duration", "canEditMarkers"],
  data() {
    return {
      mouse: { ...INIT_MOUSE },
      markers: [],
      editMarker: {},
      timeError: null,
      markerUpdateCallback: null,
      altKeyDown: false,
    }
  },

  methods: {
    eatClick() {},
    startEdit(marker) {
      this.editMarker = { ...marker, timeText: timeFormat(marker.time, 1) }
      this.timeError = null
      this.$refs["marker-edit"].show()
      this.$refs["edit-label"].focus()
      this.$nextTick(() => this.$refs["edit-label"].select())
    },
    cancelEdit() {
      if (!this.editMarker.id) {
        this.markers = this.markers.filter(m => m.id || m.time != this.editMarker.time)
      }
    },
    createMarker(ev) {
      if (!this.canEditMarkers) return

      const rect = this.$refs.markerLane.getBoundingClientRect()
      const time = ((ev.clientX - rect.left) / rect.width) * this.duration
      const marker = {
        label: "",
        time: time,
      }
      this.markers.push(marker)
      this.startEdit(marker)
    },
    async fetchMarkers() {
      const markers = await fetch(`/mixes/${this.mixID}/markers.json`)
      const json = await markers.json()
      json.markers.sort((a, b) => a.time - b.time)

      for (let i = 0; i < json.markers.length; i++) {
        json.markers[i].index = i
      }

      this.markers = json.markers
    },
    async saveMarker(marker) {
      if (marker.timeText) {
        let error
        ;[marker.time, error] = parseTimeText(marker.timeText)

        if (error) {
          this.timeError = error
          return
        }
      }

      if (this.duration && marker.time > this.duration) {
        this.timeError = `Time past end of song (${timeFormat(this.duration)})`
        return
      }

      this.timeError = null

      if (marker.id) {
        await post_csrf(`/mixes/${this.mixID}/markers/${marker.id}.json`, marker, { method: "PUT" })
      } else {
        await post_csrf(`/mixes/${this.mixID}/markers.json`, marker)
      }

      this.fetchMarkers()
      this.$refs["marker-edit"].close()
    },
    async deleteMarker(marker) {
      await post_csrf(`/mixes/${this.mixID}/markers/${marker.id}.json`, {}, { method: "DELETE" })
      this.fetchMarkers()
      this.$refs["marker-edit"].close()
    },
    markerStyle(marker) {
      if (this.duration == "undefined") return {}

      const pct = (marker.time / this.duration) * 100
      return {
        left: `calc(${pct}% - 5px)`,
        "z-index": marker.index + 10
      }
    },
    mouseDown(marker, ev) {
      if (ev.button != 0) return

      if (ev.altKey && this.canEditMarkers) {
        this.deleteMarker(marker)
        return
      }

      if (this.mouse.clicks > 0) {
        this.mouse.clicks++
        this.doubleClick(marker)
      } else {
        this.mouse.clicks++
        this.mouse.downMarker = marker
        this.mouse.mouseDownTime = new Date()
        this.mouse.mouseDownX = ev.clientX
      }
    },
    async mouseUp(ev) {
      if (ev.button != 0) return

      ev.stopPropagation()
      if (this.mouse.dragMarker) {
        const marker = this.mouse.dragMarker
        this.mouse = { ...INIT_MOUSE }
        await this.saveMarker(marker)
        return
      }

      if (this.mouse.clicks > 1) {
        // mouse-up from doubleClick, we already fired that.  bail.
        this.mouse = { ...INIT_MOUSE }
        return
      } else if (this.mouse.clicks == 1) {
        // prevent drag logic from firing
        this.mouse.mouseDownTime = undefined
      }

      setTimeout(() => {
        if (this.mouse.clicks == 1) {
          this.click(this.mouse.downMarker)
        }
        this.mouse = { ...INIT_MOUSE }
      }, DBLCLICK_WINDOW)
    },
    mouseMove(ev) {
      if (!this.mouse.mouseDownTime) return

      if (this.mouse.clicks > 1) return

      if (!this.mouse.dragMarker) {
        if (!this.canEditMarkers) return

        const sinceDown = new Date() - this.mouse.mouseDownTime

        if (sinceDown < 50) return

        const deltaX = Math.abs(ev.clientX - this.mouse.mouseDownX)
        if (deltaX < 7) return

        this.mouse.dragMarker = this.mouse.downMarker
        this.mouse.dragStart = this.$refs.markerLane.getBoundingClientRect()
      }

      const relativeX = ev.clientX - this.mouse.dragStart.left
      const xPos = Math.max(0, Math.min(relativeX / this.mouse.dragStart.width, 1))

      this.mouse.dragMarker.time = xPos * this.duration
    },
    click(marker) {
      this.$emit("marker-click", marker)
    },
    doubleClick(marker) {
      if (!this.canEditMarkers) return

      this.startEdit(marker)
    },
    handleKeyDown(ev) {
      if (ev.key === "Alt") this.altKeyDown = true
    },
    handleKeyUp(ev) {
      if (ev.key === "Alt") this.altKeyDown = false
    },
    handleWindowBlur() {
      this.altKeyDown = false
    },
    handleMarkerUpdate(data) {
      const marker = data.marker

      if (data.action === 'marker_created' || data.action === 'marker_updated') {
        const existingIndex = this.markers.findIndex(m => m.id === marker.id)

        if (existingIndex >= 0) {
          if ( marker.deleted ) {
            this.markers = this.markers.filter(m => m.id !== marker.id)
          } else {
            this.markers[existingIndex] = { ...this.markers[existingIndex], ...marker }
          }
        } else {
          this.markers.push(marker)
        }

        this.markers.sort((a, b) => a.time - b.time)
        for (let i = 0; i < this.markers.length; i++) {
          this.markers[i].index = i
        }
      }
    },
  },
  computed: {
    rulerBackground() {
      if (!this.duration) return {}

      let secondsPerTick
      if (this.duration > 0 && this.duration < 600) {
        secondsPerTick = 10
      } else {
        secondsPerTick = 60
      }

      const rulerPercentage = (secondsPerTick / this.duration) * 100

      return {
        background: `repeating-linear-gradient(
            to right,
            transparent 0px,
            transparent 1px,
            #fff 1px,
            #fff ${rulerPercentage}%
          ),
          linear-gradient(
            #FFC500 25%,
            #fff
          )`,
      }
    },
  },
  mounted() {
    this.fetchMarkers()

    this.markerUpdateCallback = this.handleMarkerUpdate.bind(this)
    mixChannelSubscription.registerCallback('marker', this.markerUpdateCallback)

    document.addEventListener("mouseup", this.mouseUp)
    document.addEventListener("mousemove", this.mouseMove)
    document.addEventListener("keydown", this.handleKeyDown)
    document.addEventListener("keyup", this.handleKeyUp)
    window.addEventListener("blur", this.handleWindowBlur)
  },
  beforeUnmount() {
    if (this.markerUpdateCallback) {
      mixChannelSubscription.unregisterCallback('marker', this.markerUpdateCallback)
    }

    document.removeEventListener("mouseup", this.mouseUp)
    document.removeEventListener("mousemove", this.mouseMove)
    document.removeEventListener("keydown", this.handleKeyDown)
    document.removeEventListener("keyup", this.handleKeyUp)
    window.removeEventListener("blur", this.handleWindowBlur)
  },
}
</script>
